/**
 * THE COMPOSER, drawn at the foot of `faithfully-kept-business/design/home/Messages.dc.html`: a
 * white bar on a hairline, the field that says "Write to Faithfully Kept", and a navy button with
 * an arrow, which sends what she wrote to the office as a `message` request (the seam, section 4).
 *
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). The request door writes the row and says
 *   sent; Messages shows it as sent to the office, and the office's answer only once ops pushes
 *   one down.
 * - HER WORDS, AS TYPED. What she wrote goes up exactly as it is, spaces and line breaks included;
 *   only a field with nothing but spaces in it sends nothing. The seam's limit, 1000 characters, is
 *   the field's own. Her words travel in the body of the request, never in a path or a query.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED. The key is minted once for the words it was minted for,
 *   so a retry after a failure names the same request and the door writes one row, and new words
 *   are a new key. While one is out, another tap does nothing.
 * - A CODE STAYS OUT. The door runs the words test before it stores anything, and a line that looks
 *   like a code is refused there: the composer says the seam's words, marks the field and gives it
 *   the focus, and keeps what she wrote so she can take the code out.
 * - WHAT THE DOOR ANSWERS decides what follows: sent is Messages' to show, and clears the field; a
 *   401 is the way to the door; a 404 says the message could not be sent here, with the phone,
 *   keeps her words and reads the place again, which goes to the page that says to call once she
 *   has no household, never silent (the owner, 2026-10-20); a message already open on the
 *   household, anybody's on it, is Messages' to say, in the composer's place, with the seam's
 *   sentence naming the open one, since trying again would be refused again; anything else says it
 *   did not go through, with the phone, and keeps her words to try again.
 * - OPENED FROM HOME. "Ask us for something" leads to Messages with `?write=1`, and the field takes
 *   the focus when it is drawn.
 * - THE FIELD STANDS AS TALL AS THE ARROW, 48, flush with it, as the artboard draws both: one line
 *   with its padding comes to less than 48 at any width, so the field's least height holds it there,
 *   and a field grown to more lines keeps its foot level with the arrow's.
 * - THE BAR RUNS THE WHOLE WIDTH, and its line and its field are in the page's one column
 *   (`column.ts`), so at a desk the field is the page's width, under it.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { sendRequest, type TooMany } from '../api.ts';
import { FAILED_BEFORE_PHONE, LOOKS_LIKE_A_CODE, MESSAGES, tooManyBeforePhone } from '../words.ts';
import { COLUMN } from './column.ts';
import { Phone } from './Phone.tsx';

/** The seam's limit on a message's body. */
export const MESSAGE_MAX = 1000;

interface ComposerProps {
  /** The household the place shows, which the message is about. */
  readonly household: string;
  /** Whether the field takes the focus when it is drawn. */
  readonly open: boolean;
  /** The door wrote the message, whose request id this is, with the words she wrote. */
  readonly onSent: (id: string, body: string) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What the place shows may be out of date: read it again. */
  readonly onStale: () => void;
  /**
   * The door refused it because a message, anybody's on the household, is still open with the
   * office, whose request id this is when the door named it.
   */
  readonly onAlreadyAsked: (requestId?: string) => void;
}

/**
 * What the composer says after a message that was not sent: a code in it, past the door's own
 * limits, the household no longer one she may write about here (the door's 404), or a failure.
 */
type Problem = 'code' | 'too_many' | 'not_here' | 'failed';

/** The artboard's arrow, pointing up. */
function Arrow() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M11 18V4M5 10l6-6 6 6" />
    </svg>
  );
}

export function Composer({
  household,
  open,
  onSent,
  onSignedOut,
  onStale,
  onAlreadyAsked,
}: ComposerProps) {
  const problemId = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Whose count of the door's own limits it was past, and when to try again, as the door said. */
  const [limit, setLimit] = useState<TooMany>();
  /** A message is out, so another tap does nothing. */
  const out = useRef(false);
  const mounted = useRef(true);
  /** The key minted for the words it was minted for. */
  const minted = useRef<{ readonly body: string; readonly key: string } | null>(null);

  useEffect(() => {
    mounted.current = true;
    if (open) field.current?.focus();
    return () => {
      mounted.current = false;
    };
    // Only when the composer is first drawn: a later render is not a new arrival.
  }, []);

  const empty = text.trim() === '';

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (out.current || empty) return;
    out.current = true;
    setProblem(null);
    const body = text;
    if (minted.current?.body !== body) minted.current = { body, key: crypto.randomUUID() };
    const answer = await sendRequest({
      key: minted.current.key,
      type: 'message',
      household,
      payload: { body },
    });
    out.current = false;
    if (!mounted.current) return;
    switch (answer.outcome) {
      case 'sent':
        minted.current = null;
        // Anything typed while it was out stays, to be sent next.
        setText((held) => (held === body ? '' : held));
        onSent(answer.id, body);
        return;
      case 'signed_out':
        onSignedOut();
        return;
      // Not hers to write about now: said over the field, her words kept, never silent, and the
      // place read again, which goes to the page that says to call once she has no household.
      case 'not_found':
        setProblem('not_here');
        onStale();
        return;
      case 'already_asked':
        onAlreadyAsked(answer.requestId);
        return;
      case 'looks_like_a_code':
        setProblem('code');
        field.current?.focus();
        return;
      case 'too_many':
        setLimit(answer.limit);
        setProblem('too_many');
        return;
      default:
        setProblem('failed');
    }
  }

  return (
    <form
      method="post"
      onSubmit={(event) => void send(event)}
      className="border-t border-border-subtle bg-surface-raised py-3"
    >
      <div className={`${COLUMN} px-4`}>
        <div aria-live="polite" id={problemId}>
          {problem === null ? null : (
            <p className="mb-2 text-small text-pretty text-text-body">
              {problem === 'code' ? (
                LOOKS_LIKE_A_CODE
              ) : (
                <>
                  {problem === 'too_many'
                    ? tooManyBeforePhone(limit)
                    : problem === 'not_here'
                      ? MESSAGES.notSentHereBeforePhone
                      : FAILED_BEFORE_PHONE}{' '}
                  <Phone after="." />
                </>
              )}
            </p>
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea
            ref={field}
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={1}
            maxLength={MESSAGE_MAX}
            placeholder={MESSAGES.write}
            aria-label={MESSAGES.write}
            aria-invalid={problem === 'code' ? true : undefined}
            aria-describedby={problem === null ? undefined : problemId}
            className="field-sizing-content max-h-40 min-h-tap flex-1 resize-none rounded-control border-2 border-border-strong bg-surface-raised px-3.5 py-2 text-small text-text-body placeholder:text-text-muted"
          />
          <button
            type="submit"
            aria-label={MESSAGES.send}
            disabled={empty}
            className="flex size-12 shrink-0 items-center justify-center rounded-control bg-surface-brand text-text-on-brand hover:bg-surface-brand-strong disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Arrow />
          </button>
        </div>
      </div>
    </form>
  );
}
